import { useState, type ReactElement, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'app/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'

export type TemplatePickerItem = {
  id: string
  name: string
  /** Preview line under the name; also searched. */
  preview: string
  /** Extra text matched by search but not displayed (e.g. the raw tokened body). */
  searchText?: string
}

type TemplatePickerBaseProps = {
  /** Opens the picker. Must be a single ref-accepting element (PopoverTrigger asChild). */
  trigger: ReactElement
  items: TemplatePickerItem[]
  /** Called when the popover opens, load/refresh the backing list here. */
  onOpen: () => void
  onSelectId: (id: string) => void
  /** Destination of the "Manage templates" footer link. */
  manageHref: string
  /** Optional left-hand footer content (e.g. SMS "Save current message"). */
  footerStart?: ReactNode
  emptyLabel?: string
  /** Which side of the trigger the popover opens on. Use "top" for triggers
   *  at a modal's bottom edge so the list stays over the modal. */
  side?: `top` | `bottom`
}

/**
 * Shared searchable template popover (Command-based: arrow keys, Enter,
 * combobox ARIA). Type-specific wrappers (SmsTemplatePicker,
 * TaskTemplatePicker) bind their store, preview rendering, and footer.
 */
export function TemplatePickerBase({
  trigger,
  items,
  onOpen,
  onSelectId,
  manageHref,
  footerStart,
  emptyLabel = `No templates yet.`,
  side = `bottom`,
}: TemplatePickerBaseProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(``)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) {
      onOpen()
      setQuery(``)
    }
  }

  const q = query.trim().toLowerCase()
  const visible = items.filter(
    (t) =>
      !q ||
      t.name.toLowerCase().includes(q) ||
      t.preview.toLowerCase().includes(q) ||
      (t.searchText?.toLowerCase().includes(q) ?? false),
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        side={side}
        className="w-80 bg-surface-raised p-0"
        // Closing this popover to open a follow-up dialog must not steal
        // focus back to the trigger (same fix as AddTagShadcn).
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false} className="bg-surface-raised">
          <CommandInput placeholder="Search templates…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>{q ? `No templates match “${query.trim()}”` : emptyLabel}</CommandEmpty>
            {visible.length > 0 && (
              // key={q} workaround mirrors AddTagShadcn, cmdk caches items internally.
              <CommandGroup key={q}>
                {visible.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={t.id}
                    onSelect={() => {
                      onSelectId(t.id)
                      setOpen(false)
                    }}
                    className="flex-col items-start gap-0.5"
                  >
                    <span className="text-xs font-medium text-default">{t.name}</span>
                    <span className="line-clamp-2 text-[11px] text-subtle">{t.preview}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          <div className="flex items-center justify-between border-t border-subtle px-2 py-1.5">
            {footerStart ?? <span />}
            <Link to={manageHref} className="text-[11px] text-subtle hover:underline" onClick={() => setOpen(false)}>
              Manage templates
            </Link>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
